import { useMemo, useState } from "react";
import { ChevronDown, ChevronUp, Inbox, Search } from "lucide-react";
import { formatINR } from "../tdsConstants";

const PAGE_SIZE = 15;

export default function TdsReportsDataGrid({
  columns,
  rows,
  loading,
  emptyTitle = "No report has been generated yet.",
  emptyHint = "Choose filters above and click Generate Report.",
  onGenerate,
  totals,
}) {
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState({ key: null, dir: "asc" });
  const [page, setPage] = useState(0);
  const [hiddenCols, setHiddenCols] = useState(new Set());

  const visibleColumns = columns.filter((c) => !hiddenCols.has(c.key));

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter((row) =>
      visibleColumns.some((col) => String(row[col.key] ?? "").toLowerCase().includes(q))
    );
  }, [rows, search, visibleColumns]);

  const sorted = useMemo(() => {
    if (!sort.key) return filtered;
    return [...filtered].sort((a, b) => {
      const av = a[sort.key];
      const bv = b[sort.key];
      if (typeof av === "number" && typeof bv === "number") {
        return sort.dir === "asc" ? av - bv : bv - av;
      }
      return sort.dir === "asc"
        ? String(av).localeCompare(String(bv))
        : String(bv).localeCompare(String(av));
    });
  }, [filtered, sort]);

  const pageCount = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const paged = sorted.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  const toggleSort = (key) => {
    setSort((prev) =>
      prev.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }
    );
  };

  if (loading) {
    return (
      <div className="tdsr-grid-skeleton">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="tdsr-grid-skeleton__row" />
        ))}
      </div>
    );
  }

  if (!rows.length && onGenerate) {
    return (
      <div className="tdsr-empty">
        <div className="tdsr-empty__icon" aria-hidden="true">
          <Inbox size={32} />
        </div>
        <h3 className="tdsr-empty__title">{emptyTitle}</h3>
        <p className="tdsr-empty__hint">{emptyHint}</p>
        <button type="button" className="tds-btn tds-btn--primary" onClick={onGenerate}>
          Generate Report
        </button>
      </div>
    );
  }

  return (
    <section className="tdsr-grid-wrap">
      <div className="tdsr-grid-toolbar">
        <div className="tdsr-grid-search">
          <Search size={14} />
          <input
            type="search"
            placeholder="Search in report…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
          />
        </div>
        <div className="tdsr-grid-cols">
          {columns.map((col) => (
            <label key={col.key} className="tdsr-grid-col-toggle">
              <input
                type="checkbox"
                checked={!hiddenCols.has(col.key)}
                onChange={() => {
                  setHiddenCols((prev) => {
                    const next = new Set(prev);
                    if (next.has(col.key)) next.delete(col.key);
                    else next.add(col.key);
                    return next;
                  });
                }}
              />
              {col.label}
            </label>
          ))}
        </div>
      </div>
      <div className="tdsr-grid-scroll">
        <table className="tdsr-grid">
          <thead>
            <tr>
              {visibleColumns.map((col) => (
                <th
                  key={col.key}
                  className={col.numeric ? "num" : ""}
                  onClick={() => col.sortable !== false && toggleSort(col.key)}
                >
                  {col.label}
                  {sort.key === col.key ? (
                    sort.dir === "asc" ? (
                      <ChevronUp size={12} />
                    ) : (
                      <ChevronDown size={12} />
                    )
                  ) : null}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paged.map((row, idx) => (
              <tr key={row.id || idx}>
                {visibleColumns.map((col) => (
                  <td key={col.key} className={col.numeric ? "num" : ""}>
                    {col.render ? col.render(row) : row[col.key] ?? "—"}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          {totals && (
            <tfoot>
              <tr>
                {visibleColumns.map((col, i) => (
                  <td key={col.key} className={col.numeric ? "num" : ""}>
                    {i === 0 ? "Total" : totals[col.key] != null ? totals[col.key] : ""}
                  </td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      <div className="tdsr-grid-pager">
        <span>
          {sorted.length} record{sorted.length === 1 ? "" : "s"}
        </span>
        <div className="tdsr-grid-pager__btns">
          <button type="button" disabled={page <= 0} onClick={() => setPage((p) => p - 1)}>
            Previous
          </button>
          <span>
            Page {page + 1} of {pageCount}
          </span>
          <button
            type="button"
            disabled={page >= pageCount - 1}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      </div>
    </section>
  );
}

export function currencyCell(value) {
  return formatINR(value);
}
